import { onUpload } from '@/lib/image/uploader'
import { useState } from 'react'

interface UseImageFormReturn {
  filePreview: string | null
  isUploading: boolean
  uploadedUrl: string | null
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<string | null>
  handleDeleteImage: (inputId?: string) => void
  reset: () => void
}

export const useImageForm = (): UseImageFormReturn => {
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  // Handle file selection and upload
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<string | null> => {
    const file = e.target.files?.[0] ?? null
    if (file) {
      setIsUploading(true)

      // Show preview immediately
      const reader = new FileReader()
      reader.onloadend = () => {
        setFilePreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      try {
        // Upload the image
        const url = (await onUpload(file)) as string

        // Update with the uploaded URL
        setUploadedUrl(url)
        setFilePreview(url)
        return url
      } catch (error: unknown) {
        // If upload fails, clear the preview
        setFilePreview(null)
        setUploadedUrl(null)
        console.error('Upload failed:', error)
      } finally {
        setIsUploading(false)
      }
    }

    return null
  }

  // Handle delete image
  const handleDeleteImage = (inputId: string = 'image-upload') => {
    console.log('handleDeleteImage', inputId)

    setFilePreview(null)
    setUploadedUrl(null)

    // Reset the file input
    const fileInput = document.getElementById(inputId) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  // Reset the form
  const reset = () => {
    setFilePreview(null)
    setUploadedUrl(null)
    setIsUploading(false)
  }

  return {
    filePreview,
    isUploading,
    uploadedUrl,
    handleFileChange,
    handleDeleteImage,
    reset,
  }
}
